/**
 * 旋轉視窗（kanban 113663）算得出來的那部分規則。
 *
 * 刻意只放純函式：canvas 重繪、focus、aria-live 節點都留在 UploadEssay 的呼叫端，
 * 這裡才測得動（專案的 vitest 是 node 環境、沒有 jsdom）。
 */
import { normalizeRotation } from "./imageUtils";

/** 一次點擊轉多少度。產品決策只做順時針，沒有逆時針鈕。 */
export const ROTATION_STEP = 90;

/**
 * 下一個累計角度：順時針 90 度，連按四次精確回到 0。
 *
 * 取模不能省：停在 360 的話 CSS transform 看起來一樣正，但交卷時
 * bakeRotationIntoImage 會判成「有角度」而多重編碼一次。
 * 先 normalizeRotation 是因為來源是稀疏的 imageRotations（查不到 key 是 undefined），
 * 不收斂就會算出 NaN，canvas.rotate(NaN) 整張圖畫不出來。
 */
export function nextRotationAngle(current: number | undefined): number {
  return (normalizeRotation(current ?? 0) + ROTATION_STEP) % 360;
}

/**
 * 旋轉視窗標題要不要帶張數計數。
 * 高中上限 2 張（UploadEssay 的 originalImageCount + 新增 > 2 整批退回，PDF 每頁算 1 張），
 * 只有一張時顯示「第 1 張 / 共 1 張」是廢話。
 * （視窗原本「一次只處理最新那張」的限制已於 kanban 106497 改為逐張走完佇列。）
 */
export function shouldShowImageCounter(total: number): boolean {
  return total > 1;
}

/** 張數計數文案。index 是陣列索引（0 起算），顯示給人看的要 +1。 */
export function formatImageCounter(index: number, total: number): string {
  return `(第 ${index + 1} 張 / 共 ${total} 張)`;
}

/**
 * 旋轉視窗計數（第 N 張 / 共 M 張）的唯一資料來源（kanban 113663 QA 追修）。
 *
 * 不能吃 previewUrls.length / currentEditingImageIndex：「重設圖片」只從
 * processedGroups 拿掉一格，previewUrls / selectedImages 要到歸零才清 ——
 * 刪 1 張再上傳，previewUrls 長成 3、實際只有 2 張，視窗會顯示「第 3 張 / 共 3 張」。
 * processedGroups 是刪除當下就同步的那份清單，張數與序號一律從它推導：
 * total = groups 長度、index = 作用中 group（rotationGroupId）在清單裡的位置。
 *
 * 回傳 null = 不顯示計數：只有一張（shouldShowImageCounter 的既有規則）、
 * 沒有作用中的 group、或該 group 已被刪掉 —— 顯示錯的序號比不顯示更糟。
 */
export function resolveImageCounter(
  groups: ReadonlyArray<{ id: string }>,
  activeGroupId: string | null
): { index: number; total: number } | null {
  const total = groups.length;
  if (!shouldShowImageCounter(total)) return null;
  if (!activeGroupId) return null;
  const index = groups.findIndex((group) => group.id === activeGroupId);
  if (index < 0) return null;
  return { index, total };
}

/**
 * 按下「確認」後要處理的下一張（kanban 106497）。
 *
 * 原本視窗一次只處理「最新那張」：一份 2 頁 PDF 會一次帶進 2 個 group，
 * 視窗直接顯示「第 2 張 / 共 2 張」，第 1 頁從頭到尾沒有被給過旋轉機會，
 * 按確認就關窗 —— 照視窗指示操作的學生會把歪的第 1 頁交出去。
 * PM 2026-08-04：「內有 2 頁稿紙，在確認調整圖片方向時只能調整一面」。
 *
 * 改成佇列：確認就從待處理清單移除當前這張，還有剩的就換下一張、視窗不關。
 *
 * 用 filter 而非 shift：currentId 理論上恆為佇列頭，但「重設圖片」會把 group
 * 從清單移除，此時 currentId 可能已不在佇列裡 —— filter 讓這種情況退化成
 * 「直接跳到剩下的第一張」，不會卡在一個不存在的 id 上。
 */
export function advanceRotationQueue(
  pending: readonly string[],
  currentId: string | null
): { pending: string[]; currentId: string | null; done: boolean } {
  const rest = pending.filter((id) => id !== currentId);
  if (rest.length === 0) return { pending: [], currentId: null, done: true };
  return { pending: rest, currentId: rest[0], done: false };
}

/**
 * 旋轉視窗要畫哪一張（kanban 106497）。
 *
 * 一律以「視窗正在處理的那一組」自己帶的原圖為準,**不可**用 group 在
 * processedGroups 的位置去索引 previewUrls / selectedImages —— 「重設圖片」
 * 只在 group 歸零時才清那兩份陣列（UploadEssay removeProcessedGroup）,
 * 刪一張再補上傳兩邊就錯開,會畫出另一張、甚至已被 revokeObjectURL 的圖：
 * 使用者照著錯的圖按旋轉,角度卻烤在正確的 group 上,交出去的方向就是錯的。
 *
 * group.originalUrl / originalFile 正是交卷時 collectSubmitOriginalsWithRotation
 * 取的同一份來源,畫面與送出物因此天生不會分岔。
 *
 * fallback 只在「反查不到 group」時才用（理論上開著視窗就不會發生）,
 * 免得旋轉鈕變成按了沒反應的死鈕。
 */
export function resolveRotationSource<F>(
  groups: ReadonlyArray<{ id: string; originalUrl?: string; originalFile?: F }>,
  activeGroupId: string | null,
  fallback: { url?: string; file?: F }
): { url: string; file: F | undefined } | null {
  const group = activeGroupId
    ? groups.find((candidate) => candidate.id === activeGroupId)
    : undefined;
  if (group?.originalUrl) {
    return { url: group.originalUrl, file: group.originalFile };
  }
  if (fallback.url) return { url: fallback.url, file: fallback.file };
  return null;
}

/**
 * 旋轉後的 aria-live 播報文案。
 * 畫面上的回饋是圖片本身（刻意不做角度文字指示器），螢幕閱讀器使用者只剩這條管道。
 */
export function formatRotationAnnouncement(angle: number): string {
  return `已順時針旋轉，目前 ${normalizeRotation(angle)} 度`;
}
